import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { format } from 'date-fns';
import { Loader2, X } from 'lucide-react';
import { loadGameInsights } from '../../lib/import/gc-save';
import { GameInsightCard, InsightCardData, groupInsightsByInning } from './GameInsightCard';

/**
 * A game's saved AI insights, opened from its game card. Read-only: it shows
 * what the import wrote and never calls Claude.
 */
export function GameInsightsSheet({ game, teamId, onClose }: {
  game: any;
  teamId: string;
  onClose: () => void;
}) {
  const [insights, setInsights] = useState<InsightCardData[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    loadGameInsights(teamId, game.id)
      .then(cards => { if (live) setInsights(cards as InsightCardData[]); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [teamId, game.id]);

  const subtitle = `${game.isHome === false ? '@' : 'vs'} ${game.opponent || 'Opponent'}${game.date ? ` · ${format(new Date(game.date), 'MMM d')}` : ''}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="fixed inset-0 z-[110] bg-canvas safe-top flex flex-col"
    >
      <div className="bg-surface border-b border-hairline px-4 py-3 flex items-center gap-3">
        <button onClick={onClose} aria-label="Close" className="p-1.5 -ml-1.5 text-ink-2 hover:text-ink">
          <X className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="text-[17px] font-semibold text-ink truncate">Game Insights</h2>
          <p className="text-[12px] font-medium text-ink-2 truncate">{subtitle}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-lg mx-auto px-4 pt-4 pb-8 space-y-4">
          {failed ? (
            <p className="text-sm text-ink-2 text-center py-16">Couldn't load the insights. Check your connection and try again.</p>
          ) : !insights ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-6 h-6 text-ink-2 animate-spin" />
            </div>
          ) : insights.length === 0 ? (
            <p className="text-sm text-ink-2 text-center py-16">No insights saved for this game yet.</p>
          ) : (
            groupInsightsByInning(insights).map((group, gi) => (
              <div key={gi} className="space-y-2">
                {group.label && <p className="text-[11px] font-semibold text-ink-2 uppercase tracking-widest px-1">{group.label}</p>}
                {group.items.map((ins, i) => <GameInsightCard key={ins.id || i} insight={ins} />)}
              </div>
            ))
          )}
        </div>
      </div>
    </motion.div>
  );
}
